import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {Loner} from "../../Loner";
import {LonerSound} from "../../sound/LonerSound";
import {LonerBitmapCountUp} from "../../components/LonerBitmapCountUp";
import {MAIN_FG_ANIMATION_NAMES} from "./LonerForegroundView";
import {LonerGameAssets} from "../../LonerGameAssets";

/**
 * Created by Jerker Nord on 2022-11-09.
 */

export class LonerBigWinView extends PIXI.Container implements StageLayer {

    private static SPINE_ANIMATION_NAMES:string[] = [MAIN_FG_ANIMATION_NAMES.BIG_WIN_LEVEL_01, MAIN_FG_ANIMATION_NAMES.BIG_WIN_LEVEL_02,
        MAIN_FG_ANIMATION_NAMES.BIG_WIN_LEVEL_03, MAIN_FG_ANIMATION_NAMES.BIG_WIN_LEVEL_04, MAIN_FG_ANIMATION_NAMES.BIG_WIN_LEVEL_05];

    private static START_FADE_IN_DURATION:number = 1.246;
    private static COUNTUP_POSITION:PIXI.Point = new PIXI.Point(360, 260);
    private static COUNTUP_END_Y_POS:number = 430;
    private static BIG_WIN_WAYS_Y_POS:number = 180;

    private _countUp:LonerBitmapCountUp;
    private _preBigWinImage:PIXI.Sprite;
    private _preBigWinTL:gsap.core.Timeline;

    constructor() {
        super();
        this.name = "LonerBigWinView";
        this.init();
    }

    private init():void {
        this.visible = false;

        this._preBigWinImage = new PIXI.Sprite(Loner.assetLoader.getTextures(LonerGameAssets.BIG_WIN_PRE)[0]);
        this._preBigWinImage.anchor.set(0.5, 0.5);
        this._preBigWinImage.position.set(360, 360);
        this._preBigWinImage.visible = false;
        Loner.totalWinView.addChild(this._preBigWinImage);

        this._preBigWinTL = gsap.timeline({paused : true});
        this._preBigWinTL.add(()=>{
            this._preBigWinImage.visible = true;
            Loner.soundPlayer.playSound(LonerSound.BIG_WIN_PRE);
        });
        this._preBigWinTL.add(gsap.timeline({delay:1}));
        this._preBigWinTL.add(()=>{
            Loner.soundPlayer.stopSound(LonerSound.BIG_WIN_PRE);
        });

        this._countUp = new LonerBitmapCountUp(70, 720);
        this._countUp.scale.set(0.8, 0.8);
        this._countUp.stopAt(0);
        this._countUp.position = LonerBigWinView.COUNTUP_POSITION;
        this._countUp.alpha = 0;

        this.addChild(this._countUp);
    }

    public getPreBigWinTL():gsap.core.Timeline {
        return this._preBigWinTL.restart();
    }

    private moveCountUpUpwards():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();

        tl.to(this._countUp, {
            duration : 0.3,
            y : LonerBigWinView.COUNTUP_END_Y_POS
        });
        tl.add(() => {
            Loner.winWaysView.setYPosWays(LonerBigWinView.BIG_WIN_WAYS_Y_POS);
            Loner.winWaysView.showInitialWinWays();
        });

        return tl;
    }

    public getCountUpScaleAnimation(duration:number, ease:string, to:number, from?:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        if(from != undefined) {
            tl.add(() => {
                this._countUp.scale.set(from, from)
            });
        }
        tl.add(gsap.to(this._countUp.scale, {duration : duration, x : to, y : to, ease : ease}))

        return tl;
    }

    public precalculateScale(endValue:number):void {
        this._countUp.stopAt(0);
        this._countUp.preCalculateScale(0, endValue);
        this._countUp.y = LonerBigWinView.COUNTUP_POSITION.y;
    }

    public show():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this.visible = true;
        });

        return tl;
    }

    public showEnd():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();

        tl.add([
            this.moveCountUpUpwards(),
            () => {
                Loner.foregroundView.playBigWinLevel(MAIN_FG_ANIMATION_NAMES.BIG_WIN_END);
                this._preBigWinImage.visible = false;
                Loner.soundPlayer.stopSound(LonerSound.MULTIPLIER_COUNT_UP_BIG);
            }
        ]);
        tl.add(gsap.timeline({delay : 2}));

        return tl;
    }

    public hide():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline(),
            duration:number = 0.2;

        tl.add([
            gsap.to(this._countUp, {duration : duration, alpha : 0}),
            () => {
                Loner.winWaysView.getHideToggleWin();
                this.visible = false;
                Loner.foregroundView.clearAnimationTrack(0);
            }
        ]);

        return tl;
    }

    public getLevelAnimation(countFrom:number, countTo:number, duration:number, level:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            Loner.soundPlayer.playSound(LonerSound.BIG_WIN_LEVEL_CHANGE);
        });
        tl.add([
            () => {
                Loner.foregroundView.playBigWinLevel(LonerBigWinView.SPINE_ANIMATION_NAMES[level - 1]);
            },
            () => this._countUp.startFlicker(),
            this._countUp.getCountAnimation(countFrom, countTo, duration, true),
            () => {
                gsap.to(this._countUp, {duration : 0.2, alpha : 1});
                Loner.soundPlayer.playNonLoopSound(LonerSound.BIG_WIN_MUSICS[level - 1]);
            }
        ]);

        const scale:gsap.core.Timeline = gsap.timeline();
        scale.add(this.getCountUpScaleAnimation(0.1, "power2.in", 1.2));
        scale.add(this.getCountUpScaleAnimation(0.2, "power2.out", 1.0));
        scale.add(this.getCountUpScaleAnimation(tl.duration() - 0.3, "power2.in", 0.8));
        tl.add(scale, 0);
        return tl;
    }
}